/** Typed literals in the wizard designer's value fields.

    A value field (`with`, `set`, `params`, `param_map`, an input's or a
    variable's default, a fixed choice) holds a TEMPLATE or a LITERAL. A
    literal keeps its JSON type in the saved model — `5` is a number, `true` a
    boolean, `null` null — because the runner hands it on as it is (whole-token
    templates keep raw types, and so must a literal): a quantity of `"5"`, a
    flag of `"false"` (truthy!) are bugs nobody sees in the designer.

    The rule, applied as the author types:
    - anything with a `{` is a template → text, untouched;
    - `true` / `false` / `null` → that value;
    - a number that reads back EXACTLY as typed (`5`, `-2`, `0.25`, never
      `007`, `1.50`, `1e3` or a 20-digit code past double precision) → a number;
    - everything else → text.
    A value the author wants kept as text although it reads as a literal (an
    item code `5`) is stored as a string; the field's type chip toggles it, and
    it survives editing ("as text" is sticky). Pure — no React. */

export type LiteralKind = 'text' | 'template' | 'number' | 'boolean' | 'null' | 'json'

const NUMBER = /^-?(0|[1-9]\d*)(\.\d+)?$/

/** The typed value `text` stands for (see the rule above). */
export function parseLiteral(text: string): unknown {
  const t = String(text ?? '')
  if (t.includes('{')) return t
  const s = t.trim()
  if (s !== t) return t
  if (s === 'true') return true
  if (s === 'false') return false
  if (s === 'null') return null
  if (NUMBER.test(s) && s !== '-0') {
    const n = Number(s)
    if (Number.isFinite(n) && String(n) === s) return n
  }
  return t
}

/** What a value field shows for a stored value. */
export function literalText(v: unknown): string {
  if (v === undefined) return ''
  if (v === null) return 'null'
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  try { return JSON.stringify(v) } catch { return String(v) }
}

/** A string that WOULD read as a typed literal — so it is text only because
 *  the author asked for text. */
export function isForcedText(v: unknown): boolean {
  return typeof v === 'string' && v !== '' && parseLiteral(v) !== v
}

export function literalKind(v: unknown): LiteralKind {
  if (v === null) return 'null'
  if (typeof v === 'number') return 'number'
  if (typeof v === 'boolean') return 'boolean'
  if (typeof v === 'string') return v.includes('{') ? 'template' : 'text'
  return v === undefined ? 'text' : 'json'
}

/** The value after the author typed `text` into a field that held `prev`:
 *  text stays text when they had chosen text, otherwise the rule applies. */
export function nextLiteral(text: string, prev: unknown): unknown {
  if (isForcedText(prev)) return text
  return parseLiteral(text)
}

/** The type chip's toggle: a typed literal becomes the same text, a text
 *  that reads as a literal becomes the literal. Anything else is unchanged. */
export function toggleLiteral(v: unknown): unknown {
  if (typeof v === 'number' || typeof v === 'boolean' || v === null) return literalText(v)
  if (isForcedText(v)) return parseLiteral(v as string)
  return v
}

/** A launch input's / variable's default for its declared type: `number` and
 *  `boolean` are converted when the text is one, `string` / `date` stay text,
 *  no type → the literal rule. Blank → undefined (no default). */
export function typedDefault(text: string, type?: string): unknown {
  if (text === '') return undefined
  if (type === 'string' || type === 'date') return text
  if (type === 'number') {
    const v = parseLiteral(text)
    return typeof v === 'number' ? v : text
  }
  if (type === 'boolean') {
    const v = parseLiteral(text.toLowerCase())
    return typeof v === 'boolean' ? v : text
  }
  return parseLiteral(text)
}
